/**
 * Pomodoro (spec §9.6): `tracking.pomodoro {phase, phaseStartedAt, cycle}`. Nothing ticks into the doc
 * (I-6): remaining time is computed from `phaseStartedAt` on every device; whichever device observes a
 * phase end first writes the transition, and concurrent duplicates resolve by LWW to the same value
 * because the next phase and its start are derived from the old state, not from the writer's clock.
 */
import { LoroMap, type LoroDoc } from "loro-crdt";
import { commitMessage } from "../core/actor";
import { C } from "../core/schema";

export type Phase = "work" | "short" | "long";

export interface PomodoroState {
  phase: Phase;
  phaseStartedAt: string;
  cycle: number;
  /** set while paused: the phase's time stands still; resuming moves `phaseStartedAt` on by the pause */
  pausedAt?: string | null;
}

export interface PomodoroSettings {
  workMin: number;
  shortMin: number;
  longMin: number;
  cyclesBeforeLong: number;
}

export function readPomodoro(doc: LoroDoc): PomodoroState | null {
  const p = (doc.getMap(C.tracking).toJSON() as { pomodoro?: PomodoroState | null }).pomodoro;
  return p && typeof p === "object" && p.phase ? p : null;
}

export function phaseMinutes(phase: Phase, s: PomodoroSettings): number {
  return phase === "work" ? s.workMin : phase === "short" ? s.shortMin : s.longMin;
}

export function phaseEndMs(p: PomodoroState, s: PomodoroSettings): number {
  return Date.parse(p.phaseStartedAt) + phaseMinutes(p.phase, s) * 60_000;
}

export function remainingMs(p: PomodoroState, s: PomodoroSettings, nowMs: number): number {
  return Math.max(0, phaseEndMs(p, s) - (p.pausedAt ? Date.parse(p.pausedAt) : nowMs));
}

/** The state after `p` ends — a pure function of `p`, so every device computes the same transition. */
export function nextState(p: PomodoroState, s: PomodoroSettings): PomodoroState {
  const startedAt = new Date(phaseEndMs(p, s)).toISOString();
  if (p.phase === "work") {
    const long = p.cycle % s.cyclesBeforeLong === 0;
    return { phase: long ? "long" : "short", phaseStartedAt: startedAt, cycle: p.cycle };
  }
  return { phase: "work", phaseStartedAt: startedAt, cycle: p.cycle + 1 };
}

/** Catch up across several elapsed phases (a device that slept) — still deterministic. */
export function currentState(p: PomodoroState, s: PomodoroSettings, nowMs: number): PomodoroState {
  if (p.pausedAt) return p; // nothing passes while paused
  let cur = p;
  for (let i = 0; i < 1000 && phaseEndMs(cur, s) <= nowMs; i++) cur = nextState(cur, s);
  return cur;
}

function write(doc: LoroDoc, p: PomodoroState | null, op: string): void {
  const tracking = doc.getMap(C.tracking);
  if (!p) tracking.set("pomodoro", null);
  else {
    const m = tracking.setContainer("pomodoro", new LoroMap());
    m.set("phase", p.phase);
    m.set("phaseStartedAt", p.phaseStartedAt);
    m.set("cycle", p.cycle);
    m.set("pausedAt", p.pausedAt ?? null);
  }
  doc.commit({ message: commitMessage({ op }) });
}

export function startPomodoro(doc: LoroDoc, now = new Date()): void {
  write(doc, { phase: "work", phaseStartedAt: now.toISOString(), cycle: 1 }, "startPomodoro");
}

export function stopPomodoro(doc: LoroDoc): void {
  write(doc, null, "stopPomodoro");
}

export function pausePomodoro(doc: LoroDoc, now = new Date()): void {
  const p = readPomodoro(doc);
  if (!p || p.pausedAt) return;
  write(doc, { ...p, pausedAt: now.toISOString() }, "pausePomodoro");
}

/** Resume: the phase's start moves on by the length of the pause, so its remaining time is what it was. */
export function resumePomodoro(doc: LoroDoc, now = new Date()): void {
  const p = readPomodoro(doc);
  if (!p || !p.pausedAt) return;
  const shifted = Date.parse(p.phaseStartedAt) + (now.getTime() - Date.parse(p.pausedAt));
  write(doc, { ...p, phaseStartedAt: new Date(shifted).toISOString(), pausedAt: null }, "resumePomodoro");
}

/** Writes the transition if the stored phase has ended. Returns the new state when it advanced. */
export function advanceIfDue(doc: LoroDoc, s: PomodoroSettings, nowMs: number): PomodoroState | null {
  const p = readPomodoro(doc);
  if (!p || p.pausedAt || phaseEndMs(p, s) > nowMs) return null;
  const next = currentState(p, s, nowMs);
  write(doc, next, "pomodoroPhase");
  return next;
}
